import { useCallback, useEffect, useMemo, useState } from 'react';
import Table from '@cloudscape-design/components/table';
import Header from '@cloudscape-design/components/header';
import Button from '@cloudscape-design/components/button';
import Badge from '@cloudscape-design/components/badge';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import TextFilter from '@cloudscape-design/components/text-filter';
import Toggle from '@cloudscape-design/components/toggle';
import { Override, getOverrides } from './api';
import EditModal from './EditModal';

interface Props {
  idToken: string;
}

export default function OverridesTable({ idToken }: Props) {
  const [items, setItems] = useState<Override[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Override | null>(null);
  const [showCustomOnly, setShowCustomOnly] = useState(false);
  const [aliasFilter, setAliasFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await getOverrides(idToken));
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const visibleItems = useMemo(() => {
    let filtered = items;
    if (showCustomOnly) {
      filtered = filtered.filter((i) => !i.includeInScheduling && i.priorityScore !== 50);
    }
    if (aliasFilter) {
      const lower = aliasFilter.toLowerCase();
      filtered = filtered.filter((i) => i.testerAlias.toLowerCase().includes(lower));
    }
    return filtered;
  }, [items, showCustomOnly, aliasFilter]);

  return (
    <>
      <Table
        loading={loading}
        loadingText="Loading overrides"
        items={visibleItems}
        filter={
          <TextFilter
            filteringPlaceholder="Filter by tester alias"
            filteringText={aliasFilter}
            onChange={({ detail }) => setAliasFilter(detail.filteringText)}
          />
        }
        header={
          <Header
            counter={`(${visibleItems.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="m">
                <Toggle checked={showCustomOnly} onChange={({ detail }) => setShowCustomOnly(detail.checked)}>
                  Show available testers only
                </Toggle>
                <Button iconName="refresh" onClick={() => void load()} />
              </SpaceBetween>
            }
          >
            Overrides
          </Header>
        }
        empty="No overrides found"
        columnDefinitions={[
          {
            id: 'alias',
            header: 'Tester Alias',
            cell: (item) => <strong>{item.testerAlias}</strong>,
            sortingField: 'testerAlias',
          },
          {
            id: 'addedTags',
            header: 'Added Tags',
            cell: (item) => (
              <SpaceBetween direction="horizontal" size="xxs">
                {item.addedTags.map((t) => (
                  <Badge key={t} color="green">
                    {t}
                  </Badge>
                ))}
              </SpaceBetween>
            ),
          },
          {
            id: 'removedTags',
            header: 'Removed Tags',
            cell: (item) => (
              <SpaceBetween direction="horizontal" size="xxs">
                {item.removedTags.map((t) => (
                  <Badge key={t} color="red">
                    {t}
                  </Badge>
                ))}
              </SpaceBetween>
            ),
          },
          {
            id: 'includeInScheduling',
            header: 'In Scheduling',
            cell: (item) =>
              item.includeInScheduling ? (
                <StatusIndicator type="success">Yes</StatusIndicator>
              ) : (
                <StatusIndicator type="error">No</StatusIndicator>
              ),
          },
          {
            id: 'priorityScore',
            header: 'Priority Score',
            cell: (item) => item.priorityScore,
            sortingField: 'priorityScore',
          },
          {
            id: 'actions',
            header: 'Actions',
            cell: (item) => (
              <Button variant="inline-link" onClick={() => setEditing(item)}>
                Edit
              </Button>
            ),
          },
        ]}
      />
      {editing && (
        <EditModal
          override={editing}
          idToken={idToken}
          onDismiss={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </>
  );
}
